import { CardType } from 'Prototype/Level/Card/CardType';
import { BoosterType } from 'State/Card/BoosterType';
import CardStateHelper, { isLevelComplete, setNewTurnCardState } from 'State/Card/CardStateHelper';
import { IGameState } from 'State/Game/IGameState';
import {
    SimulatorLevelMetrics,
    SimulatorMetrics,
    calculateFinalValue,
    getEmptySimulatorLevelMetrics,
} from '../SimulatorMetrics';
import { ILevelDataFile } from 'System/Level/Data/ILevelDataFile';
import { initialState } from 'State/Game/initialState';
import GameStateHelper from 'State/Game/GameStateHelper';
import LevelStateHelper from 'State/Level/LevelStateHelper';
import { levelStart } from 'State/Level/Action/LevelStart';
import { levelIntro } from 'State/Level/Action/LevelIntro';
import { levelActive } from 'State/Level/Action/LevelAcive';
import { LevelStatus } from 'State/Level/LevelStatus';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';

export abstract class Persona {
    abstract enabled: boolean;
    abstract readonly id: string;
    abstract readonly playThrough: boolean;
    protected abstract selectCardToPlay(state: IGameState, matches: string[]): [IGameState, string];

    public async simulate(
        levelData: ILevelDataFile,
        attempts: number,
        updateStatus: (persona: string, complete: number, attempts: number) => void
    ): Promise<SimulatorMetrics> {
        updateStatus(this.id, 0, attempts);

        const metrics: SimulatorMetrics = {
            player: this.id,
            attempts: [],
        } as SimulatorMetrics;

        let attempt = 0;

        return new Promise<SimulatorMetrics>((resolve) => {
            const simulate = (): void => {
                let t = performance.now();

                while (attempt++ < attempts) {
                    // prepare the level
                    let state = initialState;

                    state = GameStateHelper.loadLevel(state, {
                        levelNumber: levelData.settings.level_number,
                        preBoosters: []
                    });
                    state = levelStart(state, levelData);
                    state = levelIntro(state);
                    state = levelActive(state);

                    if (!metrics.level) {
                        metrics.level = {
                            id: levelData.id,
                            cardsInPlay: state.levelState.cardsState.inPlay.length,
                            cardsInStack: state.levelState.cardsState.inStack.length,
                            wildcards: Object.values(state.levelState.cardsState.cards).filter(
                                (c) => c.type !== CardType.value
                            ).length,
                            modifiers: Object.values(state.levelState.cardsState.cards).filter(
                                (c) => c.modifiers.length > 0
                            ).length,
                        };
                    }

                    // Play top stack card.
                    const inStack = state.levelState.cardsState.inStack;
                    const topCardID = inStack[inStack.length - 1];
                    state = CardStateHelper.playCard(state, topCardID);

                    // new metrics for this attempt.
                    const levelMetrics: SimulatorLevelMetrics = getEmptySimulatorLevelMetrics();

                    // play the level
                    state = this.play(state, levelMetrics);

                    // Finalize metrics.
                    levelMetrics.stackCardsRemaining = state.levelState.cardsState.inStack.length;

                    // If the level was won, complete it and update the score.
                    if (levelMetrics.result === 'complete') {
                        state = LevelStateHelper.levelComplete(state);

                        levelMetrics.score = state.levelState.score;
                        levelMetrics.stars = state.levelState.stars.filter(n => n <= state.levelState.score).length;
                    }

                    metrics.attempts.push(levelMetrics);

                    const n = performance.now();
                    // don't consume more than 500ms per frame to not completely lock the browser
                    if (n - t > 500) {
                        updateStatus(this.id, attempt, attempts);
                        requestAnimationFrame(simulate);
                        return;
                    }
                }

                // calculate averages and medians
                const completedGames = metrics.attempts.filter((attempt) => attempt.result === 'complete') ?? [];
                const wins = completedGames.length;

                metrics.result = {
                    attempts: metrics.attempts.length,
                    wins,
                };

                if (wins > 0) {
                    metrics.result.score = calculateFinalValue(completedGames, 'score');
                    metrics.result.plus5Booster = calculateFinalValue(completedGames, 'plus5BoostersPurchased');
                }

                console.log(metrics);

                resolve(metrics);
            };

            requestAnimationFrame(simulate);
        });
    }

    private play(state: IGameState, metrics: SimulatorLevelMetrics): IGameState {
        let currentFoldStreak = 0;
        let currentStreak = 0;

        while (!isLevelComplete(state)) {
            state = setNewTurnCardState(state);

            const matches = CardStateHelper.getCardMatches(state);
            if (matches.length === 0) {
                currentStreak = 0;
                if (state.levelState.cardsState.inStack.length > 0) {
                    const topCardID =
                        state.levelState.cardsState.inStack[state.levelState.cardsState.inStack.length - 1];
                    state = CardStateHelper.playCard(state, topCardID);
                    metrics.totalFolds++;
                    currentFoldStreak++;
                    if (currentFoldStreak === 2) {
                        metrics.totalFoldStreaks++;
                    }
                    if (currentFoldStreak > metrics.longestFoldStreak) {
                        metrics.longestFoldStreak = currentFoldStreak;
                    }
                } else if (this.playThrough) {
                    const price = state.levelState.boosterPrices[BoosterType.PLUS_FIVE];
                    if (state.playerState.inventory.coins < price) {
                        state = {
                            ...state,
                            playerState: InventoryStateHelper.setCoins(state.playerState, state.playerState.inventory.coins + price),
                        };
                    }

                    metrics.plus5BoostersPurchased++;
                    metrics.coinsSpent += price;
                    state = CardStateHelper.playBooster(state, BoosterType.PLUS_FIVE, {});
                } else {
                    state = GameStateHelper.endLevel(state, { status: LevelStatus.FAILED, firstComplete: false });
                    metrics.result = 'fail';
                    return state;
                }
            } else {
                currentFoldStreak = 0;
                metrics.totalMatches++;
                currentStreak++;
                if (currentStreak === 2) {
                    metrics.totalStreaks++;
                }
                if (currentStreak > metrics.longestStreak) {
                    metrics.longestStreak = currentStreak;
                }
                let matchCardID: string;
                [state, matchCardID] = this.selectCardToPlay(state, matches);
                state = CardStateHelper.playCard(state, matchCardID);
            }

            state = {
                ...state,
                levelState: {
                    ...state.levelState,
                    score: state.levelState.score + state.levelState.cardsState.turnScore,
                },
            };
        }

        metrics.result = 'complete';
        return state;
    }
}
